import React from "react";
import {
  Avatar,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Tooltip,
  Box,
  Typography,
  Button,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import { SouthAfricanRand } from "../../utils/helpers";
import { CustomerCategory } from "../../utils/constants";
import { COLORS } from "../../constants";

const CartSummaryGrid = ({
  items,
  pricingData,
  customerCategory,
  handleQuantityChange,
  handleIncrement,
  handleDecrement,
  onRemoveItemClick,
  errorMessages,
  totalItems,
  clearCart,
}) => {
  return (
    <Box sx={{}}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            sx={{
              paddingLeft: 0,
              backgroundColor: "none",
              fontSize: "var(--font-size-l)",
            }}
          >
            Cart
          </Typography>
          <Typography
            sx={{
              position: "relative",
              top: -10,
              left: 5,
              fontSize: "var(--font-size-xxs)",
            }}
          >
            ({totalItems})
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={clearCart}
          startIcon={<RemoveShoppingCartIcon fontSize="small" />}
          sx={{
            whiteSpace: "nowrap",
            borderColor: COLORS.themeYellow,
            color: COLORS.themeBlack,
            backgroundColor: COLORS.themeYellow,
            borderRadius: "2px",
            fontSize: "var(--font-size-xs)",
          }}
        >
          Clear cart
        </Button>
      </Box>
      {items.map((item) => {
        const productLink = `/product/${encodeURIComponent(item.sku)}`;
        const itemPricingData = pricingData.find((p) => p.sku === item.sku);

        return (
          <Grid
            container
            key={item.sku}
            sx={{
              border: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.white,
              borderRadius: "2px",
              mb: 3,
            }}
          >
            {/* First Row: Avatar, Name, SKU, and Delete Button */}
            <Grid
              item
              xs={12}
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${COLORS.border}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Link href={productLink}>
                  <Avatar
                    src={item.image}
                    alt={item.name}
                    variant="square"
                    sx={{
                      width: { xs: 60, md: 80 },
                      height: { xs: 60, md: 80 },
                    }}
                    imgProps={{
                      style: {
                        objectFit: "contain",
                      },
                    }}
                  />
                </Link>
                <Box>
                  <Link href={productLink} underline="hover">
                    <Typography
                      fontWeight="400"
                      sx={{
                        color: COLORS.darkText,
                        fontSize: "var(--font-size-xxs)",
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Link>
                  <Typography
                    sx={{
                      fontSize: "var(--font-size-xxs)",
                      color: COLORS.gray,
                    }}
                  >
                    SKU: {item.sku}
                  </Typography>
                </Box>
              </Box>
              <Tooltip title="Remove item" arrow sx={{}}>
                <IconButton
                  color="error"
                  onClick={() => onRemoveItemClick(item.sku)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>

            {/* Second Row: Price */}
            <Grid
              item
              xs={12}
              textAlign="center"
              sx={{
                p: 1,
                borderBottom: `1px solid ${COLORS.border}`,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                fontWeight="500"
                sx={{
                  color: COLORS.darkText,
                  fontSize: "var(--font-size-xxs)",
                }}
              >
                {SouthAfricanRand.format(itemPricingData.price)}
              </Typography>
            </Grid>

            {/* Third Row: Quantity Adjustment */}
            <Grid
              item
              xs={12}
              textAlign="center"
              sx={{
                p: 1,
                borderBottom: `1px solid ${COLORS.border}`,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <TextField
                variant="outlined"
                type="number"
                value={item.quantity}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                    backgroundColor: COLORS.background,
                    borderRadius: "99px",
                  },
                }}
                onChange={(e) =>
                  handleQuantityChange(item.id, item.sku, e.target.value)
                }
                inputProps={{
                  min: 1,
                  max: 500,
                  step: 1,
                  "aria-label": "quantity",
                  style: {
                    textAlign: "center",
                    color: COLORS.darkText,
                    fontSize: "var(--font-size-xxs)",
                  },
                }}
                error={!!errorMessages[item.sku]}
                helperText={errorMessages[item.sku]}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        onClick={() =>
                          handleDecrement(item.id, item.sku, item.quantity)
                        }
                        disabled={item.quantity <= 1}
                        sx={{ padding: 0 }}
                      >
                        <RemoveIcon
                          sx={{
                            color: COLORS.darkText,
                            fontSize: "var(--font-size-xxs)",
                          }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          handleIncrement(item.id, item.sku, item.quantity)
                        }
                        disabled={item.quantity >= 500}
                        sx={{ padding: 0 }}
                      >
                        <AddIcon
                          sx={{
                            color: COLORS.darkText,
                            fontSize: "var(--font-size-xxs)",
                          }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Fourth Row: Total Price, Invoice Price, and VAT */}
            <Grid
              item
              xs={12}
              textAlign="center"
              sx={{
                p: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                fontWeight="500"
                sx={{
                  textAlign: "center",
                  color: COLORS.darkText,
                  fontSize: "var(--font-size-xxs)",
                }}
              >
                {SouthAfricanRand.format(itemPricingData.price * item.quantity)}
              </Typography>
              {itemPricingData.invoicePrice > 0 &&
                itemPricingData.invoicePrice !== itemPricingData.price && (
                  <Typography
                    component="p"
                    sx={{
                      color: COLORS.darkText,
                      fontSize: "var(--font-size-xxs)",
                    }}
                  >
                    Invoice:{" "}
                    {SouthAfricanRand.format(
                      itemPricingData.invoicePrice * item.quantity
                    )}
                  </Typography>
                )}
              {customerCategory !== CustomerCategory.Retail && (
                <Typography component="p" variant="caption">
                  Ex. VAT
                </Typography>
              )}
            </Grid>
          </Grid>
        );
      })}
    </Box>
  );
};

export default CartSummaryGrid;
